/**
 * ProgressBar Component
 *
 * Horizontal and circular progress visualization with labels and percentages.
 */

import { useTranslation } from 'react-i18next';
import { formatAmount } from '../../utils/numberFormat';

export interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'error' | 'gradient';
  animated?: boolean;
  className?: string;
}

export default function ProgressBar({
  value,
  max = 100,
  label,
  showPercentage = false,
  size = 'md',
  variant = 'default',
  animated = false,
  className = '',
}: ProgressBarProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeClasses: Record<Required<ProgressBarProps>['size'], { bar: string; text: string }> = {
    sm: { bar: 'h-1.5', text: 'text-xs' },
    md: { bar: 'h-2.5', text: 'text-sm' },
    lg: { bar: 'h-4', text: 'text-base' },
  };

  const variantClasses: Record<Required<ProgressBarProps>['variant'], string> = {
    default: 'bg-[var(--pv-color-primary)]',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    gradient: 'bg-[var(--pv-color-primary)]',
  };

  return (
    <div className={`w-full ${className}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {(label || showPercentage) && (
        <div className={`flex items-center justify-between mb-1.5 ${sizeClasses[size].text}`}>
          {label && (
            <span className="text-[var(--pv-color-text-muted)] font-medium">{label}</span>
          )}
          {showPercentage && (
            <span className="text-[var(--pv-color-text)] font-semibold">
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      )}

      <div
        className={`w-full bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded-full overflow-hidden ${sizeClasses[size].bar}`}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
      >
        <div
          className={`${sizeClasses[size].bar} ${variantClasses[variant]} rounded-full transition-all duration-500 ${animated ? 'animate-pulse' : ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Circular progress variant
export interface CircularProgressProps {
  value: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  strokeWidth?: number;
  showValue?: boolean;
  label?: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'gradient';
  className?: string;
}

export function CircularProgress({
  value,
  max = 100,
  size = 'md',
  strokeWidth = 4,
  showValue = true,
  label,
  variant = 'default',
  className = '',
}: CircularProgressProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeValues: Record<Required<CircularProgressProps>['size'], { size: number; text: string }> = {
    sm: { size: 40, text: 'text-xs' },
    md: { size: 64, text: 'text-sm' },
    lg: { size: 96, text: 'text-lg' },
    xl: { size: 128, text: 'text-xl' },
  };

  const { size: dimensions, text: textSize } = sizeValues[size];
  const radius = (dimensions - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const variantColors: Record<Required<CircularProgressProps>['variant'], string> = {
    default: 'stroke-[var(--pv-color-primary)]',
    success: 'stroke-green-500',
    warning: 'stroke-amber-500',
    error: 'stroke-red-500',
    gradient: 'stroke-[url(#gradient)]',
  };

  return (
    <div className={`inline-flex flex-col items-center gap-1 ${className}`}>
      <div className="relative" style={{ width: dimensions, height: dimensions }}>
        <svg
          className="transform -rotate-90"
          width={dimensions}
          height={dimensions}
          aria-hidden="true"
        >
          {/* Gradient definition */}
          {variant === 'gradient' && (
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--pv-color-primary)" />
                <stop offset="100%" stopColor="var(--pv-color-accent)" />
              </linearGradient>
            </defs>
          )}

          {/* Background circle */}
          <circle
            className="stroke-gray-200 dark:stroke-[var(--pv-color-surface-soft)]"
            strokeWidth={strokeWidth}
            fill="transparent"
            r={radius}
            cx={dimensions / 2}
            cy={dimensions / 2}
          />

          {/* Progress circle */}
          <circle
            className={`${variant === 'gradient' ? '' : variantColors[variant]} transition-all duration-500`}
            stroke={variant === 'gradient' ? 'url(#gradient)' : undefined}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="transparent"
            r={radius}
            cx={dimensions / 2}
            cy={dimensions / 2}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: isRTL ? -strokeDashoffset : strokeDashoffset,
            }}
          />
        </svg>

        {showValue && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-bold text-[var(--pv-color-text)] ${textSize}`}>
              {percentage.toFixed(0)}%
            </span>
          </div>
        )}
      </div>

      {label && (
        <span className="text-sm text-[var(--pv-color-text-muted)]">{label}</span>
      )}
    </div>
  );
}

// Usage progress (used/total display)
export function UsageProgress({
  used,
  total,
  label,
  unit = '',
  variant = 'default',
  className = '',
}: {
  used: number;
  total: number;
  label?: string;
  unit?: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'gradient';
  className?: string;
}) {
  const percentage = (used / total) * 100;

  // Auto-select variant based on usage
  const autoVariant = variant === 'default'
    ? percentage >= 90 ? 'error' : percentage >= 70 ? 'warning' : 'default'
    : variant;

  return (
    <div className={className}>
      <ProgressBar
        value={used}
        max={total}
        label={label}
        variant={autoVariant}
      />
      <div className="flex justify-between mt-1 text-xs text-[var(--pv-color-text-muted)]">
        <span>
          {formatAmount(used)} {unit}
        </span>
        <span>
          {formatAmount(total)} {unit}
        </span>
      </div>
    </div>
  );
}
